/**
 * Keeps the keyboard's place across a full redraw (docs/review/keyboard-walk.md K1). The app redraws the whole page on
 * most changes (`root.replaceChildren`), which removes the focused control: without this the focus falls to <body> and
 * the next Tab starts again at the top of the page (WCAG 2.4.3). Before a redraw `markFocus` notes the focused
 * control's id; after it, when nothing has the focus any more, `restoreFocus` puts it on the redrawn control with the
 * same id, or, when that control is gone (a removed file's "Remove"), on the screen's heading.
 *
 * Fields that take typing are not taken back to: their `change` fires when Tab leaves them, and taking the focus back
 * there would hold the person in the field they just left. When the redraw came from that Tab (K9), the focus goes on
 * to the redrawn field's neighbour in the Tab order, once the browser's own move has failed (`watchTab` notes the key).
 * Structural types only, so the logic is unit-tested without a DOM (src/ui/focusKeeper.test.ts).
 */

interface Focusable {
  id: string;
  tagName: string;
  isConnected: boolean;
  focus(options?: { preventScroll?: boolean }): void;
  getAttribute(name: string): string | null;
}

interface FocusRoot {
  contains(node: unknown): boolean;
  querySelector(selector: string): unknown;
}

interface FocusDoc {
  activeElement: unknown;
  body: unknown;
  getElementById(id: string): unknown;
}

/** Where the focus was before a redraw; `leaving`: a typing field the person was leaving with Tab (forward or back). */
export interface FocusMark {
  id: string;
  leaving?: 'forward' | 'back';
}

/**
 * A Tab pressed and not yet acted on, and the control it was pressed on: `change` and the redraw it starts run inside the
 * key's own focus move. Only that control counts as being left (a redraw later in the same task, after the focus has
 * reached the next field, keeps the focus there).
 */
let pendingTab: { way: 'forward' | 'back'; from: unknown } | undefined;

/** Notes a plain Tab (or Shift+Tab) on `e.target` until the browser has moved the focus (one task later). */
export function noteTab(e: { key: string; shiftKey: boolean; altKey: boolean; ctrlKey: boolean; metaKey: boolean; target: unknown }, later: (f: () => void) => void = (f) => setTimeout(f, 0)): void {
  if (e.key !== 'Tab' || e.altKey || e.ctrlKey || e.metaKey) return;
  const tab = { way: e.shiftKey ? 'back' as const : 'forward' as const, from: e.target };
  pendingTab = tab;
  later(() => {
    if (pendingTab === tab) pendingTab = undefined;
  });
}

/** True while a Tab pressed on `el` is moving the focus away: a redraw then must not take the focus back to it. */
export const tabLeaving = (el: unknown): boolean => pendingTab !== undefined && pendingTab.from === el;

/** Watches the keyboard for Tab (capture phase, before any field's own handlers). Once, at start. */
export function watchTab(doc: { addEventListener(type: 'keydown', f: (e: KeyboardEvent) => void, capture: boolean): void }): void {
  doc.addEventListener('keydown', (e) => noteTab(e), true);
}

/** The headings a step or page opens on (they take the focus by script, tabindex -1). */
export const HEADING_SELECTOR = 'main h2[tabindex="-1"], main h1[tabindex="-1"]';

const NOT_TYPING = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'range', 'color', 'image']);

/** True for a control the person types into (text fields, text areas), where `change` fires on leaving it. */
export function takesTyping(el: Pick<Focusable, 'tagName' | 'getAttribute'>): boolean {
  const tag = el.tagName.toUpperCase();
  if (tag === 'TEXTAREA') return true;
  if (tag !== 'INPUT') return el.getAttribute('contenteditable') === 'true';
  return !NOT_TYPING.has((el.getAttribute('type') ?? 'text').toLowerCase());
}

const asFocusable = (x: unknown): Focusable | undefined =>
  x && typeof x === 'object' && typeof (x as Focusable).focus === 'function' && typeof (x as Focusable).tagName === 'string' ? (x as Focusable) : undefined;

/** Notes the focused control inside `root` (undefined when the focus is elsewhere: on <body>, in a dialog, in a field being typed in). */
export function markFocus(doc: FocusDoc, root: FocusRoot): FocusMark | undefined {
  const active = asFocusable(doc.activeElement);
  if (!active || doc.activeElement === doc.body || !root.contains(active)) return undefined;
  if (!takesTyping(active)) return { id: active.id };
  return pendingTab && pendingTab.from === active && active.id ? { id: active.id, leaving: pendingTab.way } : undefined;
}

/** After a redraw: when the focus was lost (on <body> or a removed node), back on the control with the same id (scrolled into view if the redraw moved it out), else the heading (no scroll). */
export function restoreFocus(doc: FocusDoc, root: TabRoot, mark: FocusMark | undefined, later: (f: () => void) => void = (f) => setTimeout(f, 0)): void {
  if (!mark) return;
  if (mark.leaving) {
    const way = mark.leaving;
    later(() => moveOn(doc, root, mark.id, way));
    return;
  }
  const now = asFocusable(doc.activeElement);
  if (now && doc.activeElement !== doc.body && now.isConnected) return;
  const again = mark.id ? asFocusable(doc.getElementById(mark.id)) : undefined;
  // The same control: scrolled into view when the redraw moved it out (the browser scrolls only then), as a tap's own
  // focus would; a later focus() on it by the control's handler is a no-op and cannot do it (a year chip, K1 fix).
  // The heading: no scroll, the person's place on the page is kept.
  if (again?.isConnected && root.contains(again)) again.focus();
  else asFocusable(root.querySelector(HEADING_SELECTOR))?.focus({ preventScroll: true });
}

/** What `moveOn` and `keepTabInside` need of a control in the Tab order. */
export interface TabStop extends Focusable {
  name?: string | undefined;
  type?: string | undefined;
  checked?: boolean | undefined;
}

interface TabRoot extends FocusRoot {
  querySelectorAll(selector: string): ArrayLike<unknown>;
}

const TABBABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

const shown = (x: unknown): boolean => {
  const r = (x as { getClientRects?: () => ArrayLike<unknown> }).getClientRects;
  return typeof r !== 'function' || r.call(x).length > 0;
};

/**
 * The controls Tab stops on, in order: a radio group is one stop (its checked radio, or its first going forward and
 * its last going back); hidden ones (no boxes, as inside a closed <details>) are skipped.
 */
export function tabStops(all: readonly TabStop[], way: 'forward' | 'back'): TabStop[] {
  const radio = (x: TabStop) => x.tagName.toUpperCase() === 'INPUT' && x.type === 'radio' && Boolean(x.name);
  return all.filter((x) => {
    if (x.getAttribute('tabindex') === '-1') return false;
    if (!radio(x)) return true;
    const group = all.filter((y) => radio(y) && y.name === x.name);
    const on = group.find((y) => y.checked);
    return x === (on ?? (way === 'forward' ? group[0] : group[group.length - 1]));
  });
}

const stopsIn = (root: TabRoot, way: 'forward' | 'back'): TabStop[] =>
  tabStops(Array.from(root.querySelectorAll(TABBABLE)).filter(shown).map((x) => asFocusable(x)).filter((x): x is TabStop => Boolean(x)), way);

/**
 * K9: after Tab left a typing field whose `change` redrew the page, the browser's move went to a removed control. When
 * the focus is lost (on <body>, a removed node or the heading, where Tab never stops), it goes to the redrawn field's
 * neighbour in the Tab order; the heading when the field is gone too.
 */
export function moveOn(doc: FocusDoc, root: TabRoot, id: string, way: 'forward' | 'back'): void {
  const now = asFocusable(doc.activeElement);
  const heading = asFocusable(root.querySelector(HEADING_SELECTOR));
  if (now && doc.activeElement !== doc.body && now.isConnected && now !== heading) return;
  const field = asFocusable(doc.getElementById(id));
  const stops = field?.isConnected ? stopsIn(root, way) : [];
  const at = field ? stops.indexOf(field as TabStop) : -1;
  const next = at < 0 ? undefined : stops[way === 'forward' ? at + 1 : at - 1];
  (next ?? heading)?.focus();
}

/**
 * Tab wraps inside a modal dialog (K6, K7): from its last control back to its first, and Shift+Tab from its first (or its
 * heading, which takes the focus on opening) to its last, rather than out to the browser's own controls; a focus
 * outside the dialog comes back in.
 */
export function keepTabInside(dialog: TabRoot & { addEventListener(type: 'keydown', f: (e: KeyboardEvent) => void): void }, doc: FocusDoc): void {
  dialog.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab' || e.altKey || e.ctrlKey || e.metaKey) return;
    const stops = stopsIn(dialog, e.shiftKey ? 'back' : 'forward');
    const first = stops[0];
    const last = stops[stops.length - 1];
    if (!first || !last) return;
    const at = stops.indexOf(doc.activeElement as TabStop);
    const outside = !dialog.contains(doc.activeElement);
    const to = e.shiftKey ? (at <= 0 ? last : undefined) : (at === stops.length - 1 || outside ? first : undefined);
    if (!to) return;
    e.preventDefault();
    to.focus();
  });
}
